<template>
  <div>
    <div v-if="stage === 'capture' && !ignoreCapture">
      <div
        v-if="!paymentLoading"
        class="row"
        style="padding-top: 0px; padding-bottom: 0px;"
      >
        <div class="col-lg-12" v-if="requireBillReference">
          <b-input-group class="payment-input">
            <b-form-input
              size="sm"
              v-model="requestReference"
              placeholder="Bill Reference Number (Optional)"
              type="text"
            ></b-form-input>
          </b-input-group>
        </div>
        <div class="col-md-12" style="margin-top: 20px;">
          <b-input-group size="lg" prepend="£" class="payment-input">
            <b-form-input
              placeholder="Amount"
              v-model="requestAmount"
              type="number"
              step="0.01"
              min="0.01"
              max="500.00"
            ></b-form-input>
          </b-input-group>

          <div v-if="isDebt" class="debt-options">
            <div>
              Please note: <span>{{ debtPercent }}&percnt;</span> of each top-up
              will be taken for debt recovery
            </div>

            <div v-if="allowFullDebt" class="button-option">
              <b-form-checkbox v-model="isFullDebt" switch
                >Apply full top-up amount to debt recovery
                balance</b-form-checkbox
              >
            </div>
          </div>
        </div>
      </div>

      <div class="payment-button">
        <b-button
          v-on:click="requestPayment"
          v-if="!paymentLoading"
          variant="outline-info"
          >Continue</b-button
        >
        <div v-if="paymentLoading">
          <b-spinner variant="primary" label="Spinning"></b-spinner>
        </div>
      </div>

      <PaymentNotes
        ref="paymentNotes"
        v-if="!paymentLoading"
        @payment="setPayment"
      ></PaymentNotes>
    </div>

    <div v-if="stage === 'payment'">
      <p v-if="requestReference" class="payment-requests">
        <strong>Bill Reference Number: </strong>
        {{ requestReference }}
      </p>

      <div v-if="isDebt" class="debt-options">
        <span>Note:</span> This top-up is set to apply
        <span v-if="isFullDebt">in full</span
        ><span v-else>{{ debtPercent }}&percnt;</span> to debt recovery
        collection.
      </div>

      <StripePayment
        ref="stripePayment"
        @complete="setPaymentComplete"
        v-if="paymentType === 'stripe'"
        :amount="requestAmount"
        :gtag-reference="gtagReference"
        :is-credit="isCredit"
      ></StripePayment>
    </div>

    <div v-if="stage === 'complete'">
      <b-alert variant="success" show>
        <strong>Payment Complete</strong>
        <div>
          We have received your payment and are updating your balance
        </div>
        <div style="margin-top: 10px;">
          <b-button
            v-if="allowFurtherPayment"
            class="action"
            size="sm"
            @click="newPayment"
            variant="success"
            >Make further payment</b-button
          >
        </div>
      </b-alert>
    </div>
  </div>
</template>

<script>
import PaymentNotes from "@/views/Shared/PaymentNotes.vue";
import StripePayment from "@/views/Components/Payment/StripePayment.vue";

export default {
  name: "PaymentChoice",
  props: {
    gtagReference: {
      type: String,
      required: true
    },
    requireBillReference: {
      type: Boolean,
      default: false
    },
    allowFurtherPayment: {
      type: Boolean,
      default: true
    },
    ignoreCapture: {
      type: Boolean,
      default: false
    },
    isCredit: {
      type: Boolean,
      default: false
    },
    debtPercent: {
      type: String,
      default: null
    },
    allowFullDebt: {
      type: Boolean,
      default: false
    }
  },
  components: { StripePayment, PaymentNotes },
  computed: {
    isDebt() {
      return this.debtPercent && this.debtPercent > 0;
    }
  },
  data() {
    return {
      stage: "capture",
      paymentLoading: false,
      paymentType: "stripe",
      paymentSecret: null,
      requestReference: null,
      requestAmount: null,
      minAmount: 0.4,
      maxAmount: 500,
      isFullDebt: false
    };
  },
  methods: {
    setPayment(payment) {
      this.paymentType = payment;
    },
    newPayment() {
      this.stage = "capture";
      this.$emit("complete");
      this.$nextTick(() => {
        this.$refs.paymentNotes.setDefault();
      });
    },
    resetPayment() {
      this.requestAmount = null;
      this.paymentSecret = null;
      this.requestReference = null;
      this.paymentLoading = false;
      this.isFullDebt = false;
    },
    setPaymentComplete() {
      this.stage = "complete";
      this.resetPayment();
    },
    loadPayment(secret, amount) {
      this.paymentSecret = secret;
      if (!this.requestAmount && amount) {
        this.requestAmount = amount;
      }

      this.stage = "payment";
      this.$nextTick(() => {
        this.loadPaymentStage();
      });
    },
    loadPaymentStage() {
      if (this.paymentType === "stripe") {
        this.$refs.stripePayment.loadPayment(this.paymentSecret);
      }
    },
    requestPayment() {
      this.paymentLoading = true;

      if (this.requestReference) {
        this.requestReference = this.requestReference.toUpperCase();
      }

      let amount = this.requestAmount;
      let errorReason = null;

      if (!amount || isNaN(amount)) {
        errorReason = "Please enter a valid payment amount";
      }

      if (
        !errorReason &&
        Math.floor(amount) !== amount &&
        amount.toString().split(".")[1] !== undefined &&
        amount.toString().split(".")[1].length > 2
      ) {
        errorReason = "Please enter a valid payment amount";
      }

      if (!errorReason && amount < this.minAmount) {
        errorReason = "The minimum payment is £0.40";
      }

      if (!errorReason && amount > this.maxAmount) {
        errorReason = "The maximum payment is £500.00";
      }

      if (errorReason) {
        const title = "Payment Error";
        const subtitle = errorReason;
        const samePage = true;
        this.$store.dispatch("pushError", { title, subtitle, samePage });
        this.paymentLoading = false;
        return;
      }

      this.requestAmount = amount = parseFloat(amount).toFixed(2);

      this.$emit("handleRequest", {
        amount: this.requestAmount,
        billReference: this.requestReference,
        isFullDebt: this.isFullDebt
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../../scss/dashboard.scss";

.payment-input input {
  border-radius: 0;
}

.payment-requests {
  text-align: left;
  font-size: 0.8rem;
}

.debt-options {
  text-align: left;
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 0.9rem;

  span {
    font-weight: bold;
  }

  .button-option {
    margin-top: 10px;
  }
}
</style>
